/**
 * Agent Chat Component
 * Real-time chat interface with WebSocket for streaming agent execution
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Audiotrack as AudioIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Menu as MenuIcon,
  Stop as StopIcon,
  TableChart as TableChartIcon,
  ExpandMore as ExpandMoreIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { markSubAgentEventsStopped } from '../../utils/subAgentEvents';
import 'highlight.js/styles/github-dark.css';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';

import { agentApi } from '../../services/api';
import { authService } from '../../services/auth';
import { PageHeader, LoadingSpinner } from '../common';
import AgentSessionList from './AgentSessionList';
import useVoiceChat from '../../hooks/useVoiceChat';
import { MicButton, VoiceOverlay, VoiceSettings } from '../voice';
import AttachmentMessage from '../chat/AttachmentMessage';
import SubAgentEventsTree from '../chat/SubAgentEventsTree';
import ConversationDetailsButton from '../chat/ConversationDetailsButton';
import stripMarkdown from '../../utils/stripMarkdown';
import { getTextDirection, muiBidiTextSx } from '../chat/textDirection';
import {
  canAgentAcceptAudio,
  canAgentSpeakAudio,
  getAgentAudioInputDisabledReason,
} from '../../utils/audioInputSupport';
import {
  attachSubAgentEvent,
  flushPendingSubAgentEvents,
  isSubAgentEvent,
} from '../../utils/subAgentEvents';
import {
  applyToolResultToMessages,
  bufferPendingToolResult,
  clearPendingToolResults,
  flushPendingToolResults,
  hasPendingToolCallMessages,
  mergePersistedToolResults,
} from '../../utils/toolCallEvents';
import type { WebSocketMessage, ExecutionUpdate, ConversationMessage, MessageAttachment } from '../../types';

interface ChatMessage {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL_CALL' | 'ASSISTANT_STATUS';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  toolName?: string;
  toolArgs?: Record<string, any>;
  toolResult?: any;
  toolSuccess?: boolean;
  toolCallId?: string;
  toolRationale?: string;
  toolGroupRationale?: string;
  subAgentEvents?: any[];
  validation?: any;
  executionId?: string;
  toolNames?: string[];
  voiceReady?: boolean;
}

const STOP_PENDING_ACTIVITY_TYPES = new Set([
  'tools_loaded',
  'iteration_started',
  'llm_thinking',
  'assistant_status',
  'assistant_chunk',
  'llm_response',
  'assistant_message',
  'message_received',
  'tool_call',
  'tool_result',
  'execution_data',
  'validator_result',
]);

const STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES = new Set([
  'execution_started',
  'assistant_status',
  'tool_call',
  'llm_response',
]);

function shouldIgnoreDuringStop(data: any) {
  if (!data) return false;
  if (isSubAgentEvent(data)) {
    return STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES.has(data.type);
  }
  return STOP_PENDING_ACTIVITY_TYPES.has(data.type);
}

const isCancelledToolResult = (result: any) => Boolean(
  result && (result.cancelled === true || result.data?.cancelled === true)
);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const getAttachmentIcon = (type: MessageAttachment['attachment_type']) => {
  switch (type) {
    case 'IMAGE':
      return <ImageIcon fontSize="small" />;
    case 'DOCUMENT':
      return <DescriptionIcon fontSize="small" />;
    case 'CODE':
      return <CodeIcon fontSize="small" />;
    case 'DATA':
      return <TableChartIcon fontSize="small" />;
    case 'AUDIO':
      return <AudioIcon fontSize="small" />;
    default:
      return <FileIcon fontSize="small" />;
  }
};

const createStoppedToolResult = () => ({
  success: false,
  data: { cancelled: true },
  error: 'Execution cancelled by user.',
});

const markPendingToolCallsStopped = (messages: ChatMessage[]): ChatMessage[] => {
  let changed = false;
  const next = messages.map((message) => {
    let updated = message;

    if (Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0) {
      const nested = markSubAgentEventsStopped(message.subAgentEvents);
      if (nested !== message.subAgentEvents) {
        updated = { ...updated, subAgentEvents: nested };
        changed = true;
      }
    }

    if (
      updated.role !== 'TOOL_CALL' ||
      (updated.toolResult !== undefined && updated.toolResult !== null)
    ) {
      return updated;
    }

    changed = true;
    return {
      ...updated,
      toolResult: createStoppedToolResult(),
      toolSuccess: false,
    };
  });

  return changed ? next : messages;
};

/* ── ToolCallCard ── collapsible per-tool card ──────────────────────────── */
const ToolCallCard: React.FC<{ message: ChatMessage; isDark: boolean }> = ({ message, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const hasResult = message.toolResult !== undefined && message.toolResult !== null;
  const isCancelled = isCancelledToolResult(message.toolResult);
  const success = message.toolSuccess === true;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5,
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
        }}
      >
        <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', flex: 1 }}>
          {message.toolName}
        </Typography>
        <Chip
          label={!hasResult ? 'Running...' : isCancelled ? 'Stopped' : success ? 'Done' : 'Error'}
          size="small"
          color={!hasResult ? 'warning' : isCancelled ? 'default' : success ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
        <ExpandMoreIcon
          sx={{
            fontSize: 16, color: 'text.secondary',
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
      {expanded && (
        <Box sx={{ borderTop: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', p: 1 }}>
          {message.toolRationale && (
            <Box
              sx={{
                mb: message.toolArgs && Object.keys(message.toolArgs).length > 0 ? 0.75 : 0,
                color: 'text.secondary',
                fontSize: '0.78rem',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {message.toolRationale}
            </Box>
          )}
          {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
            <Box sx={{ mb: hasResult ? 0.75 : 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Arguments</Typography>
              <Box sx={{
                mt: 0.25, p: 0.75, borderRadius: 0.5, fontSize: '0.75rem',
                fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                maxHeight: 120, overflow: 'auto',
              }}>
                {JSON.stringify(message.toolArgs, null, 2)}
              </Box>
            </Box>
          )}
          {hasResult && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Result</Typography>
              <Box sx={{
                mt: 0.25, p: 0.75, borderRadius: 0.5, fontSize: '0.75rem',
                fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                color: success ? 'success.main' : isCancelled ? 'text.secondary' : 'error.main',
                maxHeight: 200, overflow: 'auto',
              }}>
                {typeof message.toolResult === 'string' ? message.toolResult : JSON.stringify(message.toolResult, null, 2)}
              </Box>
            </Box>
          )}
          {Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0 && (
            <Box sx={{ mt: hasResult || (message.toolArgs && Object.keys(message.toolArgs).length > 0) ? 0.75 : 0 }}>
              <SubAgentEventsTree events={message.subAgentEvents} />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

/* ── ToolCallGroup ── groups consecutive tool calls into a collapsible container ── */
const ToolCallGroup: React.FC<{ toolMsgs: ChatMessage[]; isDark: boolean }> = ({ toolMsgs, isDark }) => {
  const [collapsed, setCollapsed] = useState(true);
  const allDone = toolMsgs.every(m => m.toolResult !== undefined && m.toolResult !== null);
  const cancelledCount = toolMsgs.filter(m => isCancelledToolResult(m.toolResult)).length;
  const allCancelled = allDone && cancelledCount === toolMsgs.length;
  const anyFailed = toolMsgs.some(m => m.toolSuccess === false && !isCancelledToolResult(m.toolResult));
  const statusLabel = !allDone ? 'Running...' : allCancelled ? 'Stopped' : (anyFailed || cancelledCount > 0) ? 'Partial' : 'Done';
  const statusColor: 'warning' | 'success' | 'error' | 'default' = !allDone ? 'warning' : allCancelled ? 'default' : anyFailed ? 'error' : cancelledCount > 0 ? 'warning' : 'success';
  const toolNames = toolMsgs.map(m => m.toolName).join(', ');
  const groupRationale = toolMsgs.find(m => m.toolGroupRationale)?.toolGroupRationale
    || (toolMsgs.length === 1 ? toolMsgs[0].toolRationale : undefined);

  return (
    <Box sx={{ mb: 1, ml: { xs: 4, md: 6 } }}>
      <Paper
        sx={{
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          bgcolor: isDark ? 'rgba(33, 150, 243, 0.04)' : 'rgba(33, 150, 243, 0.03)',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
          }}
        >
          <Typography sx={{
            fontSize: '10px', color: 'text.secondary',
            transition: 'transform 0.15s ease',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', lineHeight: 1,
          }}>▶</Typography>
          <Typography sx={{ fontSize: '13px' }}>🔧</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
            {toolMsgs.length} tool call{toolMsgs.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {toolNames}
          </Typography>
          <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }} />
        </Box>
        {groupRationale && (
          <Box
            sx={{
              px: 1.5,
              pb: 0.75,
              mt: -0.25,
              color: 'text.secondary',
              fontSize: '0.78rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {groupRationale}
          </Box>
        )}
        {!collapsed && (
          <Box sx={{
            borderTop: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.75,
          }}>
            {toolMsgs.map(msg => <ToolCallCard key={msg.id} message={msg} isDark={isDark} />)}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/* ── groupMessages ── groups consecutive TOOL_CALL messages ─────────────── */
type GroupedItem = ChatMessage | { type: 'tool_group'; id: string; msgs: ChatMessage[] };
function groupMessages(messages: ChatMessage[]): GroupedItem[] {
  const result: GroupedItem[] = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'TOOL_CALL') {
      const group: ChatMessage[] = [];
      while (i < messages.length && messages[i].role === 'TOOL_CALL') {
        group.push(messages[i]);
        i++;
      }
      result.push({ type: 'tool_group', id: `tg-${group[0].id}`, msgs: group });
    } else {
      result.push(messages[i]);
      i++;
    }
  }
  return result;
}

interface AgentChatProps {
  agentIdProp?: number;
  hideHeader?: boolean;
  embeddedVariant?: 'default' | 'builder';
  embeddedHeaderTitle?: string;
  embeddedHeaderSubtitle?: string;
  headerActions?: React.ReactNode;
  contextPreamble?: string;
  executionContext?: Record<string, any>;
  onRealtimeEvent?: (event: WebSocketMessage) => void;
}

export const AgentChat: React.FC<AgentChatProps> = ({
  agentIdProp,
  hideHeader = false,
  embeddedVariant = 'default',
  embeddedHeaderTitle,
  embeddedHeaderSubtitle,
  headerActions,
  contextPreamble,
  executionContext,
  onRealtimeEvent,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentId = agentIdProp ?? parseInt(id || '0');
  const agentChatAccess = useActionAccess(AI_ACTION_SURFACES.agentChatSend);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBuilderEmbedded = hideHeader && embeddedVariant === 'builder';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentSessionData, setCurrentSessionData] = useState<any | null>(null);
  const [liveUsage, setLiveUsage] = useState<{
    executionId: string | null;
    sessionId: string | null;
    totalTokens: number;
    totalCost: number;
  }>({
    executionId: null,
    sessionId: null,
    totalTokens: 0,
    totalCost: 0,
  });
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const currentExecutionIdRef = useRef<string | null>(null);
  const isExecutingRef = useRef(false);
  const pendingSubAgentEventsRef = useRef<Record<string, any[]>>({});
  const pendingToolResultsRef = useRef<Record<string, any[]>>({});
  const pendingToolRationaleRef = useRef<string | null>(null);
  const stopPendingRef = useRef(false);
  const onRealtimeEventRef = useRef(onRealtimeEvent);

  const reconcileExecutionToolMessages = useCallback(async (executionId: string) => {
    try {
      const persistedMessages = await agentApi.getExecutionMessages(executionId);
      setMessages((prev) => mergePersistedToolResults(prev, persistedMessages) as typeof prev);
    } catch (error) {
      console.warn('[AgentChat] Failed to reconcile persisted tool messages:', error);
    }
  }, []);

  useEffect(() => {
    onRealtimeEventRef.current = onRealtimeEvent;
  }, [onRealtimeEvent]);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    currentExecutionIdRef.current = currentExecutionId;
  }, [currentExecutionId]);

  useEffect(() => {
    isExecutingRef.current = isExecuting;
  }, [isExecuting]);

  // Fetch agent details before deriving voice/audio support from the model.
  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentApi.get(agentId),
    enabled: !!agentId,
  });

  const audioInputSupported = canAgentAcceptAudio(agent);
  const audioInputDisabledReason = getAgentAudioInputDisabledReason(agent);
  const voiceButtonDisabled = agentChatAccess.disabled || !audioInputSupported;
  const voiceButtonDisabledReason = agentChatAccess.disabled
    ? agentChatAccess.blockedMessage
    : audioInputDisabledReason;

  const buildOutboundMessage = useCallback((content: string) => {
    if (!contextPreamble || currentSessionId || messages.length > 0) {
      return content;
    }
    return `${contextPreamble}\n\n${content}`;
  }, [contextPreamble, currentSessionId, messages.length]);

  const pendingVoiceResponseRef = useRef(false);
  const voiceResponseBaselineRef = useRef<string | null>(null);
  const spokenVoiceExecutionIdsRef = useRef<Set<string>>(new Set());

  const handleVoiceRecording = useCallback(async (file: File) => {
    if (!isConnected || isExecuting) return;
    if (!agentChatAccess.ensureAccess()) return;
    if (!audioInputSupported) {
      toast.error(audioInputDisabledReason);
      return;
    }

    try {
      const lastAssistant = [...messages].reverse().find((m) => m.role === 'ASSISTANT' && m.content);
      pendingVoiceResponseRef.current = true;
      voiceResponseBaselineRef.current = lastAssistant?.id || null;
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const messageContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      const outboundMessage = buildOutboundMessage(messageContent);
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'USER',
        content: messageContent,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const wsMessage: WebSocketMessage = {
        type: 'execute',
        message: outboundMessage,
        session_id: currentSessionId || undefined,
        attachment_ids: [attachment.id],
        context: { ...executionContext, attachment_ids: [attachment.id] },
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(wsMessage));
        setIsExecuting(true);
      } else {
        toast.error('Not connected to agent');
      }
    } catch (error) {
      console.error('Voice recording upload failed:', error);
      toast.error('Failed to send voice recording');
      pendingVoiceResponseRef.current = false;
      throw error;
    }
  }, [agentChatAccess, audioInputDisabledReason, audioInputSupported, buildOutboundMessage, currentSessionId, executionContext, isConnected, isExecuting, messages]);

  // Voice chat — record and send audio to the LLM
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    ttsVoice, setTtsVoice,
    voiceRate, setVoiceRate,
    isRecordingSupported, isTTSSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
    onSpeakResponse: canAgentSpeakAudio(agent)
      ? async (text, options) => {
          if (!options.executionId) {
            throw new Error('Voice output requires a completed execution.');
          }
          return agentApi.synthesizeSpeech(options.executionId, {
            text,
            voice: options.voice,
            speed: options.speed,
          });
        }
      : undefined,
  });

  // Speak the completed assistant response for the active voice turn.
  useEffect(() => {
    if (!isVoiceOn || !pendingVoiceResponseRef.current) return;

    const lastMsg = messages.length > 0
      ? [...messages].reverse().find((m) => m.role === 'ASSISTANT' && m.content)
      : undefined;
    const baselineId = voiceResponseBaselineRef.current;
    const isNewVoiceResponse = Boolean(lastMsg && lastMsg.id !== baselineId);

    if (lastMsg && isNewVoiceResponse && lastMsg.voiceReady && lastMsg.executionId) {
      if (!spokenVoiceExecutionIdsRef.current.has(lastMsg.executionId)) {
        spokenVoiceExecutionIdsRef.current.add(lastMsg.executionId);
        pendingVoiceResponseRef.current = false;
        speakResponse(stripMarkdown(lastMsg.content), lastMsg.executionId);
      }
      return;
    }

    if (!isExecuting && voiceState === 'processing' && isNewVoiceResponse && !lastMsg?.executionId) {
      pendingVoiceResponseRef.current = false;
      stopVoice();
    }
  }, [isExecuting, isVoiceOn, messages, speakResponse, stopVoice, voiceState]);

  // If execution ends without an assistant message (timeout/error),
  // reset voice state so UI does not stay on "Processing...".
  const prevIsExecutingRef = useRef(false);
  useEffect(() => {
    const wasExecuting = prevIsExecutingRef.current;
    prevIsExecutingRef.current = isExecuting;
    if (wasExecuting && !isExecuting && voiceState === 'processing') {
      const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
      if (!lastMsg || lastMsg.role !== 'ASSISTANT') {
        stopVoice();
      }
    }
  }, [isExecuting, voiceState, messages, stopVoice]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load session from localStorage on mount only
  useEffect(() => {
    const savedSessionId = localStorage.getItem(`agent_${agentId}_session`);
    if (savedSessionId) {
      setCurrentSessionId(savedSessionId);
      loadSessionMessages(savedSessionId);
    }
  }, [agentId]);

  // Save current session to localStorage
  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem(`agent_${agentId}_session`, currentSessionId);
    } else {
      localStorage.removeItem(`agent_${agentId}_session`);
    }
  }, [currentSessionId, agentId]);

  // Load messages from a session
  const loadSessionMessages = async (sessionId: string) => {
    try {
      console.log('Loading session:', sessionId);
      const session = await agentApi.getSession(sessionId);
      console.log('Session loaded:', session);
      console.log('Session messages:', session.messages);
      setCurrentSessionData(session);
      setLiveUsage({
        executionId: null,
        sessionId,
        totalTokens: 0,
        totalCost: 0,
      });
      
      if (session.messages && session.messages.length > 0) {
        const loadedMessages: ChatMessage[] = session.messages.map((msg: any): ChatMessage | null => {
          if (msg.role === 'TOOL_CALL' || msg.role === 'tool_call') {
            return {
              id: msg.id?.toString() || `tool-${Date.now()}`,
              role: 'TOOL_CALL' as const,
              content: '',
              timestamp: new Date(msg.created_at),
              toolName: msg.tool_name,
              toolArgs: msg.tool_input,
              toolResult: msg.tool_output,
              toolSuccess: msg.success,
              toolCallId: msg.tool_call_id || msg.id?.toString(),
              toolRationale: (msg.tool_rationale || msg.toolRationale || '').trim() || undefined,
              toolGroupRationale: (msg.tool_group_rationale || msg.toolGroupRationale || '').trim() || undefined,
              subAgentEvents: Array.isArray(msg.sub_agent_events)
                ? msg.sub_agent_events
                : [],
            };
          }
          if (msg.role === 'ASSISTANT_STATUS' || msg.role === 'assistant_status') {
            return null;
          }
          return {
            id: msg.id.toString(),
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            executionId: msg.execution || undefined,
          };
        }).filter((message): message is ChatMessage => Boolean(message));
        console.log('Loaded messages:', loadedMessages);
        pendingSubAgentEventsRef.current = {};
        clearPendingToolResults(pendingToolResultsRef.current);
        pendingToolRationaleRef.current = null;
        setMessages(loadedMessages);
        // toast.success(`Loaded ${loadedMessages.length} messages`); // Disabled - too frequent
      } else {
        console.log('No messages in session');
        pendingSubAgentEventsRef.current = {};
        clearPendingToolResults(pendingToolResultsRef.current);
        pendingToolRationaleRef.current = null;
        setMessages([]);
        // toast.info('This conversation has no messages yet'); // Disabled - unnecessary
      }
    } catch (error) {
      console.error('Failed to load session messages:', error);
      toast.error('Failed to load conversation history');
    }
  };

  // WebSocket connection
  useEffect(() => {
    if (!agentId) return;

    // Get auth token
    const token = authService.getAccessToken();
    if (!token) {
      toast.error('Authentication required');
      navigate('/login');
      return;
    }

    // WebSocket URL - connect to Django backend
    // In production (HTTPS), use default port (443) so nginx can proxy to Django
    // In development (HTTP), use port 8000 directly
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname;
    const isProduction = window.location.protocol === 'https:';
    const wsPort = isProduction ? '' : ':8000';
    const wsUrl = `${wsProtocol}//${wsHost}${wsPort}/ws/agents/${agentId}/chat/?token=${token}`;

    console.log('Connecting to WebSocket:', wsUrl);

    let reconnectTimer: ReturnType<typeof window.setTimeout> | null = null;
    let reconnectAttempts = 0;
    let closedByEffect = false;
    const maxReconnectAttempts = 8;
    const terminalStatuses = new Set(['COMPLETED', 'ERROR', 'CANCELLED', 'FAILED', 'STOPPED']);

    const recoverAfterConnect = async () => {
      const activeSessionId = currentSessionIdRef.current;
      const activeExecutionId = currentExecutionIdRef.current;

      if (activeSessionId) {
        await loadSessionMessages(activeSessionId);
      }

      if (!activeExecutionId) return;

      try {
        const execution = await agentApi.getExecutionSummary(activeExecutionId);
        const status = String(execution.status || '').toUpperCase();
        await reconcileExecutionToolMessages(activeExecutionId);

        if (terminalStatuses.has(status)) {
          stopPendingRef.current = false;
          setIsExecuting(false);
          setCurrentExecutionId(null);
          if (execution.session_id || activeSessionId) {
            await loadSessionMessages(execution.session_id || activeSessionId!);
          }
          return;
        }

        setIsExecuting(true);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'subscribe',
            execution_id: activeExecutionId,
          }));
        }
      } catch (error) {
        console.warn('[AgentChat] Failed to recover websocket state:', error);
      }
    };

    const connectSocket = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        const wasReconnect = reconnectAttempts > 0;
        reconnectAttempts = 0;
        if (wasReconnect || currentSessionIdRef.current || isExecutingRef.current) {
          void recoverAfterConnect();
        }
        // toast.success('Connected to agent'); // Disabled - too frequent
      };

      ws.onmessage = (event) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data);
        console.log('WebSocket message:', data);

        if (stopPendingRef.current && shouldIgnoreDuringStop(data)) {
          console.log('Ignoring late activity after stop request:', data.type);
          return;
        }

        onRealtimeEventRef.current?.(data);

        if (isSubAgentEvent(data)) {
          setMessages((prev) => {
            const updated = attachSubAgentEvent(prev, data);
            if (updated === prev) {
              const key = data.parent_tool_call_id;
              const pending = pendingSubAgentEventsRef.current;
              if (!pending[key]) pending[key] = [];
              pending[key].push(data);
              return prev;
            }
            return flushPendingSubAgentEvents(updated, pendingSubAgentEventsRef.current) as typeof prev;
          });
          return;
        }

        if (data.type === 'connected') {
          // Connection confirmed
          console.log('Connection confirmed');
        } else if (data.type === 'session_started') {
          // Backend created/loaded session
          if (data.session_id && data.session_id !== currentSessionIdRef.current) {
            currentSessionIdRef.current = data.session_id;
            setCurrentSessionId(data.session_id);
          }
        } else if (data.type === 'execution_started') {
          stopPendingRef.current = false;
          setCurrentExecutionId(data.execution_id || null);
          setLiveUsage({
            executionId: data.execution_id || null,
            sessionId: (data as any).session_id || currentSessionIdRef.current,
            totalTokens: 0,
            totalCost: 0,
          });
          setIsExecuting(true);
        } else if (data.type === 'tools_loaded') {
          // Optional: show tool count
          console.log(`Tools loaded: ${data.tool_count}`);
        } else if (data.type === 'execution_completed') {
          const completedExecutionId = (data as any).execution_id || currentExecutionIdRef.current;
          const completedSessionId = (data as any).session_id || currentSessionIdRef.current;
          setLiveUsage((prev) => ({
            executionId: completedExecutionId || prev.executionId,
            sessionId: completedSessionId || prev.sessionId,
            totalTokens: Number((data as any).total_tokens ?? prev.totalTokens ?? 0),
            totalCost: Number((data as any).total_cost ?? prev.totalCost ?? 0),
          }));
          if (stopPendingRef.current && (data as any).status !== 'CANCELLED') {
            stopPendingRef.current = false;
            setMessages((prev) => markPendingToolCallsStopped(prev));
            setIsExecuting(false);
            setCurrentExecutionId(null);
            toast.info('Execution stopped');
            return;
          }
          stopPendingRef.current = false;
          if ((data as any).status === 'CANCELLED') {
            setMessages((prev) => markPendingToolCallsStopped(prev));
            setIsExecuting(false);
            setCurrentExecutionId(null);
            toast.info((data as any).message || 'Execution stopped');
            return;
          }
          if (completedExecutionId && hasPendingToolCallMessages(messages)) {
            reconcileExecutionToolMessages(completedExecutionId);
          }
          // Add assistant response message
          if (data.result && data.result.response) {
            const assistantMessage: ChatMessage = {
              id: `assistant-${Date.now()}`,
              role: 'ASSISTANT',
              content: data.result.response,
              timestamp: new Date(),
              executionId: completedExecutionId || undefined,
              voiceReady: true,
            };
            setMessages((prev) => [...prev, assistantMessage]);
          }
          setIsExecuting(false);
          setCurrentExecutionId(null);
          toast.success('Execution completed');
          if (completedSessionId) {
            agentApi.getSession(completedSessionId)
              .then((session) => setCurrentSessionData(session))
              .catch((error) => console.warn('[AgentChat] Failed to refresh session usage:', error));
          }
          
          // Auto-generate title for new sessions after first message
          if (currentSessionIdRef.current && messages.length === 1) {
            // Fire and forget - don't await to avoid blocking
            agentApi.generateSessionTitle(currentSessionIdRef.current)
              .then(() => console.log('Session title generated'))
              .catch((error) => console.error('Failed to generate title:', error));
          }
        } else if (data.type === 'execution_error') {
          const message = data.error || 'Execution error';
          stopPendingRef.current = false;
          toast.error(message);
          const errorMessage: ChatMessage = {
            id: `execution-error-${Date.now()}`,
            role: 'SYSTEM',
            content: `Execution failed\n${message}`,
            timestamp: new Date(),
            validation: {
              allowed: false,
              terminal: true,
              error: message,
            },
          };
          setMessages((prev) => [...markPendingToolCallsStopped(prev), errorMessage]);
          setIsExecuting(false);
          setCurrentExecutionId(null);
        } else if (data.type === 'execution_stopped') {
          stopPendingRef.current = false;
          setMessages((prev) => markPendingToolCallsStopped(prev));
          setIsExecuting(false);
          setCurrentExecutionId(null);
          toast.info(data.message || 'Execution stopped');
        } else if (data.type === 'stop_requested') {
          stopPendingRef.current = true;
          toast.info(data.message || 'Stop request sent');
          // Keep isExecuting true until we get completion or error
        } else if (data.type === 'assistant_status') {
          pendingToolRationaleRef.current = (data.content || '').trim() || null;
        } else if (data.type === 'tool_call') {
          // Tool being called — add a TOOL_CALL message
          setMessages((prev) => {
            const toolRationale = (data.tool_rationale || data.toolRationale || '').trim() || undefined;
            const toolGroupRationale = (data.tool_group_rationale || data.toolGroupRationale || '').trim()
              || pendingToolRationaleRef.current
              || undefined;
            pendingToolRationaleRef.current = null;
            const nextMessage: ChatMessage = {
              id: `tool-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              role: 'TOOL_CALL',
              content: '',
              timestamp: new Date(),
              toolName: data.tool_name,
              toolArgs: data.tool_args || data.arguments,
              toolCallId: (data as any).tool_call_id,
              toolRationale,
              toolGroupRationale,
              subAgentEvents: [],
            };
            const withSubAgents = flushPendingSubAgentEvents(
              [...prev, nextMessage],
              pendingSubAgentEventsRef.current,
            );
            return flushPendingToolResults(
              withSubAgents,
              pendingToolResultsRef.current,
            );
          });
        } else if (data.type === 'tool_result') {
          // Tool result: reconcile by tool_call_id first; buffer until the card exists.
          setMessages((prev) => {
            const applied = applyToolResultToMessages(prev, {
              toolName: (data as any).tool_name,
              toolCallId: (data as any).tool_call_id,
              result: data.result,
            });
            if (!applied.applied) {
              bufferPendingToolResult(pendingToolResultsRef.current, {
                toolName: (data as any).tool_name,
                toolCallId: (data as any).tool_call_id,
                result: data.result,
              });
              return prev;
            }
            return applied.messages as typeof prev;
          });
        } else if (data.type === 'execution_data') {
          setLiveUsage((prev) => ({
            executionId: (data as any).execution_id || currentExecutionIdRef.current || prev.executionId,
            sessionId: (data as any).session_id || currentSessionIdRef.current || prev.sessionId,
            totalTokens: Number((data as any).total_tokens ?? prev.totalTokens ?? 0),
            totalCost: Number((data as any).total_cost ?? prev.totalCost ?? 0),
          }));
        } else if (data.type === 'validator_result') {
          const runs = Array.isArray((data as any).runs) ? (data as any).runs : [];
          const names = runs
            .map((run: any) => run.policy_name)
            .filter(Boolean)
            .join(', ');
          const status = (data as any).allowed ? 'passed' : 'actioned';
          const message = (data as any).message || `Validator ${status}`;
          const validatorMessage: ChatMessage = {
            id: `validator-${Date.now()}`,
            role: 'SYSTEM',
            content: names
              ? `Validator ${status}: ${names}\n${message}`
              : `Validator ${status}\n${message}`,
            timestamp: new Date(),
            validation: data,
          };
          setMessages((prev) => [...prev, validatorMessage]);
        } else if (data.type === 'execution_update') {
          handleExecutionUpdate(data.data as ExecutionUpdate);
        } else if (data.type === 'error') {
          toast.error(data.error || 'Execution error');
          setIsExecuting(false);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
      setIsConnected(false);
    };

    ws.onclose = (event) => {
      console.log('WebSocket disconnected');
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      setIsConnected(false);
      const shouldKeepRecovering = Boolean(
        currentExecutionIdRef.current ||
        isExecutingRef.current
      );
      if (closedByEffect || event.code === 1000 || (reconnectAttempts >= maxReconnectAttempts && !shouldKeepRecovering)) {
        return;
      }
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
      reconnectAttempts += 1;
      reconnectTimer = window.setTimeout(connectSocket, delay);
      // toast.info('Disconnected from agent'); // Disabled - too frequent
    };
    };

    connectSocket();

    return () => {
      closedByEffect = true;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.close(1000, 'component unmounted');
      }
    };
  }, [agentId, navigate, reconcileExecutionToolMessages]);

  useEffect(() => {
    if (!isExecuting || !currentExecutionId || !hasPendingToolCallMessages(messages)) {
      return;
    }

    let cancelled = false;
    const refreshToolMessages = async () => {
      if (cancelled) return;
      await reconcileExecutionToolMessages(currentExecutionId);
    };

    refreshToolMessages();
    const interval = window.setInterval(refreshToolMessages, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentExecutionId, isExecuting, messages, reconcileExecutionToolMessages]);

  // Handle execution updates from WebSocket
  const handleExecutionUpdate = (update: ExecutionUpdate) => {
    console.log('Execution update:', update);

    if (update.type === 'message' && update.message) {
      // Add assistant message
      const newMessage: ChatMessage = {
        id: update.message.id.toString(),
        role: update.message.role,
        content: update.message.content,
        timestamp: new Date(update.message.created_at),
      };
      setMessages((prev) => [...prev, newMessage]);
    } else if (update.type === 'step' && update.step) {
      // Show step information
      if (update.step.step_type === 'REASONING') {
        pendingToolRationaleRef.current = (update.step.content || '').trim() || null;
      } else if (update.step.step_type === 'TOOL_CALL') {
        const step = update.step as any;
        const toolRationale = pendingToolRationaleRef.current || undefined;
        pendingToolRationaleRef.current = null;
        const toolMessage: ChatMessage = {
          id: `step-${step.id}`,
          role: 'TOOL_CALL',
          content: `🔧 Using tool: ${update.step.tool_name}`,
          toolName: step.tool_name,
          toolArgs: step.tool_input || {},
          toolResult: step.tool_output,
          toolSuccess: step.success,
          toolCallId: step.tool_call_id || `step-${step.id}`,
          toolRationale,
          toolGroupRationale: toolRationale,
          timestamp: new Date(step.created_at),
        };
        setMessages((prev) => [...prev, toolMessage]);
      }
    } else if (update.type === 'complete') {
      setIsExecuting(false);
      setCurrentExecutionId(null);
      toast.success('Execution completed');
    } else if (update.type === 'error') {
      setIsExecuting(false);
      setCurrentExecutionId(null);
      toast.error(update.error || 'Execution failed');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!agentChatAccess.ensureAccess()) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    try {
      const uploadedAttachments = await Promise.all(
        Array.from(files).map((file) => agentApi.uploadAttachment(file))
      );
      setPendingAttachments((prev) => [...prev, ...uploadedAttachments]);
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    try {
      await agentApi.deleteAttachment(attachmentId);
      setPendingAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      toast.error('Failed to remove attachment');
    }
  };

  // Send message
  const handleSendMessage = () => {
    if ((!inputMessage.trim() && pendingAttachments.length === 0) || !isConnected || isExecuting || isUploading) return;
    if (!agentChatAccess.ensureAccess()) return;

    let visibleContent = inputMessage;
    let messageContent = inputMessage;

    if (pendingAttachments.length > 0) {
      const fileList = pendingAttachments
        .map((attachment) => (
          `- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${
            attachment.file_url ? ` [View](${attachment.file_url})` : ''
          }`
        ))
        .join('\n');
      visibleContent = visibleContent
        ? `${visibleContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
      messageContent = messageContent
        ? `${messageContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
    }

    const attachmentIds = pendingAttachments.map((attachment) => attachment.id);
    const outboundMessage = buildOutboundMessage(messageContent);
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'USER',
      content: visibleContent,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Send to WebSocket with session_id
    const wsMessage: WebSocketMessage = {
      type: 'execute',
      message: outboundMessage,
      session_id: currentSessionId || undefined,
      ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
      context: attachmentIds.length > 0
        ? { ...executionContext, attachment_ids: attachmentIds }
        : executionContext,
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(wsMessage));
      setIsExecuting(true);
      setInputMessage('');
      setPendingAttachments([]);
      
      // Auto-generate title after first message if new session
      if (!currentSessionId) {
        // Will be set by session_started event from backend
      }
    } else {
      toast.error('Not connected to agent');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Stop execution
  const handleStopExecution = () => {
    if (!currentExecutionId || !wsRef.current) return;

    const wsMessage: WebSocketMessage = {
      type: 'stop',
      execution_id: currentExecutionId,
    };

    if (wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(wsMessage));
      stopPendingRef.current = true;
      toast.info('Stopping execution...');
    } else {
      toast.error('Not connected to agent');
    }
  };

  const handleClearChat = () => {
    pendingSubAgentEventsRef.current = {};
    clearPendingToolResults(pendingToolResultsRef.current);
    pendingToolRationaleRef.current = null;
    setMessages([]);
    setPendingAttachments([]);
    toast.info('Chat cleared');
  };

  // Handle session selection
  const handleSessionSelect = (sessionId: string | null) => {
    setCurrentSessionId(sessionId);
    currentSessionIdRef.current = sessionId;
    setPendingAttachments([]);
    if (sessionId) {
      loadSessionMessages(sessionId);
    } else {
      pendingSubAgentEventsRef.current = {};
      clearPendingToolResults(pendingToolResultsRef.current);
      pendingToolRationaleRef.current = null;
      setCurrentSessionData(null);
      setLiveUsage({ executionId: null, sessionId: null, totalTokens: 0, totalCost: 0 });
      setMessages([]);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth < 900) {
      setSidebarOpen(false);
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    // Check if current session is already empty (no messages) - don't create another
    if (messages.length === 0 && currentSessionId === null) {
      pendingSubAgentEventsRef.current = {};
      clearPendingToolResults(pendingToolResultsRef.current);
      pendingToolRationaleRef.current = null;
      setPendingAttachments([]);
      console.log('handleNewConversation - already in a fresh state, skipping');
      return;
    }
    
    console.log('handleNewConversation called - clearing session');
    // Clear localStorage immediately to prevent reload issues
    localStorage.removeItem(`agent_${agentId}_session`);
    console.log('localStorage cleared, currentSessionId was:', currentSessionId);
    
    // Clear state
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setCurrentSessionData(null);
    setLiveUsage({ executionId: null, sessionId: null, totalTokens: 0, totalCost: 0 });
    pendingSubAgentEventsRef.current = {};
    clearPendingToolResults(pendingToolResultsRef.current);
    pendingToolRationaleRef.current = null;
    setMessages([]);
    setPendingAttachments([]);
    console.log('State cleared - currentSessionId set to null, messages cleared');
    
    toast.info('Started new conversation');
    // Close sidebar on mobile
    if (window.innerWidth < 900) {
      setSidebarOpen(false);
    }
  };

  if (isLoading || !agent) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={hideHeader ? {
      // Embedded: fill parent container only
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      overflow: 'hidden',
      bgcolor: 'background.default',
      minHeight: 0,
    } : {
      // Route page: stay in normal layout flow so the shared app top bar remains visible.
      position: 'relative',
      width: '100%',
      height: 'calc(100vh - var(--topbar-height, 0px) - 16px)',
      display: 'flex',
      overflow: 'hidden',
      bgcolor: 'background.default',
      minHeight: 0,
    }}>
      {/* Session history. Builder-embedded mode uses a drawer like AI System Builder. */}
      {isBuilderEmbedded ? (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: { width: '85vw', maxWidth: 360 },
          }}
        >
          <AgentSessionList
            agentId={agentId}
            currentSessionId={currentSessionId}
            onSessionSelect={(sessionId) => { handleSessionSelect(sessionId); setSidebarOpen(false); }}
            onNewConversation={() => { handleNewConversation(); setSidebarOpen(false); }}
          />
        </Drawer>
      ) : hideHeader ? null : isMobile ? (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: { width: '85vw', maxWidth: 360 },
          }}
        >
          <AgentSessionList
            agentId={agentId}
            currentSessionId={currentSessionId}
            onSessionSelect={(sessionId) => { handleSessionSelect(sessionId); setSidebarOpen(false); }}
            onNewConversation={() => { handleNewConversation(); setSidebarOpen(false); }}
          />
        </Drawer>
      ) : (
        sidebarOpen && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
              overflow: 'hidden',
              transition: 'width 0.3s ease-in-out',
            }}
          >
            <AgentSessionList
              agentId={agentId}
              currentSessionId={currentSessionId}
              onSessionSelect={handleSessionSelect}
              onNewConversation={handleNewConversation}
            />
          </Box>
        )
      )}

      {/* Main Chat Area */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'all 0.3s ease-in-out',
          minHeight: 0,
        }}
      >
        {isBuilderEmbedded && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.75, sm: 1 },
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BotIcon sx={{ fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {embeddedHeaderTitle || 'AI Manager'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {embeddedHeaderSubtitle || `${agent.name} · ${agent.model_name || agent.model?.name || agent.model?.model || 'Model not set'}`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {headerActions}
                <ConversationDetailsButton
                  sessionId={currentSessionId}
                  sessionData={currentSessionData}
                  messages={messages}
                  agentName={agent.name}
                  modelName={agent.model_name || agent.model?.name || agent.model?.model || 'Not set'}
                  statusLabel={isExecuting ? 'Running' : (currentSessionId ? 'Idle' : 'No active session')}
                  currentExecutionId={currentExecutionId || liveUsage.executionId}
                  liveSessionId={liveUsage.sessionId || currentSessionId}
                  liveTotalTokens={liveUsage.totalTokens}
                  liveTotalCost={liveUsage.totalCost}
                  isExecuting={isExecuting}
                  buttonSx={{ p: { xs: 0.5, sm: 0.75 } }}
                  iconSx={{ fontSize: { xs: 18, sm: 22 } }}
                />
                <Chip
                  label={isConnected ? 'Connected' : 'Offline'}
                  color={isConnected ? 'success' : 'error'}
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, height: 24 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open chat history"
                  title="Chat history"
                >
                  <HistoryIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNewConversation}
                  disabled={messages.length === 0 && currentSessionId === null}
                  aria-label="New conversation"
                  title="New conversation"
                >
                  <AddIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {!hideHeader && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              px: { xs: 1.5, md: 3 },
              py: { xs: 1, md: 1.25 },
            }}
          >
            <PageHeader
              dense
              title={`Chat with ${agent.name}`}
              breadcrumbs={[
                { label: 'Agents', to: '/ai/agents' },
                { label: agent.name, to: `/ai/agents/${agentId}` },
                { label: 'Chat' },
              ]}
              actions={
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                    <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} size="small" sx={{ p: 0.75 }}>
                      <MenuIcon />
                    </IconButton>
                    <ConversationDetailsButton
                      sessionId={currentSessionId}
                      sessionData={currentSessionData}
                      messages={messages}
                      agentName={agent.name}
                      modelName={agent.model_name || agent.model?.name || agent.model?.model || 'Not set'}
                      statusLabel={isExecuting ? 'Running' : (currentSessionId ? 'Idle' : 'No active session')}
                      currentExecutionId={currentExecutionId || liveUsage.executionId}
                      liveSessionId={liveUsage.sessionId || currentSessionId}
                      liveTotalTokens={liveUsage.totalTokens}
                      liveTotalCost={liveUsage.totalCost}
                      isExecuting={isExecuting}
                      buttonSx={{ p: 0.75 }}
                    />
                  <Chip
                    label={isConnected ? 'Connected' : 'Disconnected'}
                    color={isConnected ? 'success' : 'error'}
                    size="small"
                    sx={{ height: 24 }}
                  />
                    <IconButton onClick={handleClearChat} size="small" sx={{ p: 0.75 }}>
                      <RefreshIcon />
                    </IconButton>
                    <IconButton onClick={() => navigate(`/ai/agents/${agentId}`)} size="small" sx={{ p: 0.75 }}>
                      <SettingsIcon />
                    </IconButton>
                </Box>
              }
            />
          </Box>
        )}

        {/* Chat Messages Area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: isBuilderEmbedded ? { xs: 1, sm: 2 } : hideHeader ? 1.5 : { xs: 1.5, md: 3 },
            bgcolor: 'background.default',
            backgroundImage: isDark
              ? 'linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px)',
            backgroundSize: '100% 20px',
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
                textAlign: 'center',
                px: 1,
              }}
            >
              <BotIcon sx={{ fontSize: hideHeader ? 52 : 80, mb: 2, opacity: 0.3 }} />
              <Typography variant={hideHeader ? 'subtitle1' : 'h5'} gutterBottom fontWeight={600}>
                Start a conversation
              </Typography>
              <Typography variant={hideHeader ? 'body2' : 'body1'} color="text.secondary">
                {currentSessionId ? 'Continue your conversation' : 'Ask the agent anything to get started'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxWidth: isBuilderEmbedded ? '100%' : 1000, mx: 'auto' }}>
              {groupMessages(messages).map((item) => {
                if ('type' in item && item.type === 'tool_group') {
                  return <ToolCallGroup key={item.id} toolMsgs={item.msgs} isDark={isDark} />;
                }
                const message = item as ChatMessage;
                const isValidatorMessage = message.role === 'SYSTEM' && !!message.validation;
                const isStatusMessage = message.role === 'ASSISTANT_STATUS';
                const validatorActioned = isValidatorMessage && message.validation?.allowed === false;
                const messageDirection = getTextDirection(message.content);
                return (
                <Box
                  key={message.id}
                  sx={{
                    display: isBuilderEmbedded ? 'grid' : 'flex',
                    gridTemplateColumns: isBuilderEmbedded
                      ? { xs: '28px minmax(0, 1fr)', md: '32px minmax(0, 1fr)' }
                      : undefined,
                    mb: 3,
                    flexDirection: isBuilderEmbedded ? 'row' : message.role === 'USER' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: isBuilderEmbedded ? { xs: 0.75, sm: 1 } : 0,
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'visible',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        message.role === 'USER'
                          ? 'primary.main'
                          : isStatusMessage
                          ? 'action.disabledBackground'
                          : isValidatorMessage
                          ? validatorActioned ? 'warning.main' : 'success.main'
                          : message.role === 'SYSTEM'
                          ? 'info.main'
                          : 'success.main',
                      mx: isBuilderEmbedded ? 0 : { xs: 0.5, md: 1.5 },
                      width: isBuilderEmbedded ? { xs: 28, md: 32 } : { xs: 28, md: 40 },
                      height: isBuilderEmbedded ? { xs: 28, md: 32 } : { xs: 28, md: 40 },
                      boxShadow: 1,
                      flexShrink: 0,
                    }}
                  >
                    {message.role === 'USER' ? (
                      <PersonIcon />
                    ) : isStatusMessage ? (
                      <BotIcon fontSize="small" />
                    ) : message.role === 'SYSTEM' ? (
                      <SettingsIcon fontSize="small" />
                    ) : (
                      <BotIcon />
                    )}
                  </Avatar>

                  <Box
                    sx={{
                      flex: isBuilderEmbedded ? '1 1 0' : undefined,
                      width: isBuilderEmbedded ? '100%' : undefined,
                      maxWidth: isBuilderEmbedded ? '100%' : hideHeader ? '92%' : { xs: '100%', sm: '75%' },
                      minWidth: hideHeader ? 0 : 200,
                      overflow: 'hidden',
                    }}
                  >
                    <Paper
                      elevation={message.role === 'SYSTEM' || isStatusMessage ? 0 : 2}
                      sx={{
                        p: isBuilderEmbedded ? { xs: 1, sm: 1.5 } : hideHeader ? 1.5 : 2.5,
                        bgcolor:
                          message.role === 'USER'
                            ? isBuilderEmbedded
                              ? isDark ? 'primary.900' : 'primary.50'
                              : 'primary.main'
                            : isValidatorMessage
                            ? validatorActioned
                              ? isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)'
                              : isDark ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.08)'
                            : isStatusMessage
                            ? isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.06)'
                            : message.role === 'SYSTEM'
                            ? isDark ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.06)'
                            : isBuilderEmbedded
                              ? isDark ? 'grey.800' : 'grey.50'
                              : 'background.paper',
                        color: isBuilderEmbedded ? 'text.primary' : message.role === 'USER' ? 'primary.contrastText' : 'text.primary',
                        borderRadius: isBuilderEmbedded ? 1 : 2,
                        border: message.role === 'SYSTEM' || isStatusMessage ? '1px solid' : 'none',
                        borderColor: isValidatorMessage
                          ? validatorActioned
                            ? isDark ? 'rgba(245, 158, 11, 0.45)' : 'rgba(245, 158, 11, 0.35)'
                            : isDark ? 'rgba(34, 197, 94, 0.45)' : 'rgba(34, 197, 94, 0.35)'
                          : isStatusMessage
                          ? isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(100, 116, 139, 0.16)'
                          : isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
                        boxShadow: message.role === 'SYSTEM' || isStatusMessage ? 'none' : undefined,
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 1,
                          fontWeight: 700,
                        color: isBuilderEmbedded
                          ? 'text.secondary'
                            : isValidatorMessage
                              ? validatorActioned ? 'warning.main' : 'success.main'
                            : isStatusMessage
                              ? 'text.secondary'
                            : message.role === 'SYSTEM'
                              ? 'info.main'
                              : message.role === 'USER'
                                ? 'inherit'
                                : 'success.main',
                        }}
                      >
                        {isBuilderEmbedded
                          ? message.role === 'USER' ? 'You' : isStatusMessage ? 'AI Manager' : isValidatorMessage ? 'Validator' : message.role === 'SYSTEM' ? 'System' : 'AI Manager'
                          : message.role === 'USER' ? 'You' : isStatusMessage ? 'Assistant' : isValidatorMessage ? 'Validator' : message.role === 'SYSTEM' ? 'System' : 'Assistant'}
                        {isStatusMessage && (
                          <Chip
                            label="Working"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                      </Typography>
                      <Box 
                        dir={messageDirection}
                        sx={{ 
                          ...muiBidiTextSx,
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          minWidth: 0,
                          maxWidth: '100%',
                          overflowX: 'hidden',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          '& > *': { maxWidth: '100%', boxSizing: 'border-box' },
                          '& :where(p, li, h1, h2, h3, h4, h5, h6, span, strong, em, a, th, td)': {
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          },
                          '& p': { margin: '0.5em 0', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' },
                          '& p:first-of-type': { marginTop: 0 },
                          '& p:last-of-type': { marginBottom: 0 },
                          '& pre': { 
                            direction: 'ltr',
                            unicodeBidi: 'isolate',
                            textAlign: 'left',
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            padding: '12px',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.875rem',
                            maxWidth: '100%',
                            whiteSpace: 'pre',
                          },
                          '& code': {
                            direction: 'ltr',
                            unicodeBidi: 'isolate',
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '0.875em',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          },
                          '& pre code': {
                            backgroundColor: 'transparent',
                            padding: 0,
                            overflowWrap: 'normal',
                            wordBreak: 'normal',
                            whiteSpace: 'pre',
                          },
                          '& ul, & ol': { paddingInlineStart: '1.25em', margin: '0.5em 0', overflowWrap: 'anywhere', wordBreak: 'break-word' },
                          '& li': { margin: '0.25em 0', overflowWrap: 'anywhere', wordBreak: 'break-word' },
                          '& h1, & h2, & h3, & h4, & h5, & h6': { 
                            margin: '1em 0 0.5em 0',
                            fontWeight: 600,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          },
                          '& blockquote': {
                            borderInlineStart: '4px solid',
                            borderColor: message.role === 'USER'
                              ? 'rgba(255,255,255,0.3)'
                              : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            paddingInlineStart: '1em',
                            margin: '0.5em 0',
                            fontStyle: 'italic',
                          },
                          '& a': {
                            color: message.role === 'USER' ? 'rgba(255,255,255,0.9)' : 'primary.main',
                            textDecoration: 'underline',
                          },
                          '& table': {
                            borderCollapse: 'collapse',
                            width: '100%',
                            maxWidth: '100%',
                            tableLayout: 'fixed',
                            margin: '0.5em 0',
                            whiteSpace: 'normal',
                          },
                          '& th, & td': {
                            border: '1px solid',
                            borderColor: message.role === 'USER'
                              ? 'rgba(255,255,255,0.3)'
                              : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                            padding: '8px',
                            textAlign: 'start',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          },
                          '& th': {
                            backgroundColor: message.role === 'USER'
                              ? 'rgba(0,0,0,0.2)'
                              : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {message.role === 'USER' ? (
                          <AttachmentMessage content={message.content} />
                        ) : isStatusMessage ? (
                          <Typography variant="body2" color="text.secondary">
                            {message.content}
                          </Typography>
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight, rehypeRaw]}
                          >
                            {message.content}
                          </ReactMarkdown>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          opacity: message.role === 'USER' ? 0.8 : 0.6,
                          fontSize: '0.75rem',
                        }}
                      >
                        {message.timestamp.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
                );
              })}

              {isExecuting && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    mb: 3,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'success.main', mx: 1.5, width: 40, height: 40, boxShadow: 1 }}>
                    <BotIcon />
                  </Avatar>
                  <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={5} />
                      <Typography variant="body2" color="text.secondary">
                        Agent is thinking...
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Paper sx={{ p: isBuilderEmbedded ? { xs: 1, sm: 2 } : hideHeader ? 1.25 : { xs: 1.5, md: 2.5 }, boxShadow: hideHeader ? 0 : 3, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          {!isConnected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Not connected to agent. Reconnecting...
            </Alert>
          )}

          {/* Voice overlay */}
          {isVoiceOn && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <VoiceOverlay voiceState={voiceState} interimTranscript={interimTranscript} />
            </Box>
          )}

          {pendingAttachments.length > 0 && (
            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, maxWidth: hideHeader ? '100%' : 1000, mx: 'auto' }}>
              {pendingAttachments.map((attachment) => (
                <Chip
                  key={attachment.id}
                  icon={getAttachmentIcon(attachment.attachment_type)}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: { xs: 120, sm: 180 } }}>
                        {attachment.original_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        ({formatFileSize(attachment.size)})
                      </Typography>
                    </Box>
                  }
                  onDelete={() => handleRemoveAttachment(attachment.id)}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: { xs: '100%', sm: 320 },
                    '& .MuiChip-deleteIcon': {
                      color: 'action.active',
                      '&:hover': { color: 'error.main' },
                    },
                  }}
                />
              ))}
            </Box>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
            accept=".txt,.md,.json,.csv,.py,.js,.ts,.jsx,.tsx,.html,.css,.xml,.yaml,.yml,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.svg,.webm,.wav,.mp3,.m4a,.ogg"
          />

          <Box sx={{ display: 'flex', gap: isBuilderEmbedded ? { xs: 0.5, sm: 1 } : hideHeader ? 0.75 : 1.5, maxWidth: hideHeader ? '100%' : 1000, mx: 'auto', alignItems: 'flex-end' }}>
            <Tooltip title={agentChatAccess.disabled ? agentChatAccess.blockedMessage : 'Attach file'}>
              <span>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!isConnected || isExecuting || isUploading || agentChatAccess.disabled}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    flexShrink: 0,
                    height: hideHeader ? 40 : { xs: 40, md: 56 },
                    width: hideHeader ? 40 : { xs: 40, md: 56 },
                    borderRadius: hideHeader ? 1.5 : 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  aria-label="Attach file"
                >
                  {isUploading ? <CircularProgress size={20} /> : <AttachFileIcon fontSize={isMobile ? 'small' : 'medium'} />}
                </IconButton>
              </span>
            </Tooltip>

            <TextField
              fullWidth
              multiline
              maxRows={isMobile ? 4 : 6}
              placeholder={agentChatAccess.disabled ? agentChatAccess.blockedMessage : isVoiceOn ? "Recording voice message..." : (pendingAttachments.length > 0 ? "Add a message about your files..." : (currentSessionId ? "Continue the conversation..." : "Type your message to start..."))}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!isConnected || isExecuting || agentChatAccess.disabled}
              inputProps={{ dir: getTextDirection(inputMessage) }}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                minWidth: 0,
                '& .MuiInputBase-input': {
                  ...muiBidiTextSx,
                  direction: getTextDirection(inputMessage),
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: hideHeader ? 1.5 : 2,
                  bgcolor: 'background.paper',
                  fontSize: hideHeader ? '0.9rem' : { xs: '0.9rem', md: '1rem' },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            
            {/* Mic button */}
            <MicButton
              voiceState={voiceState}
              isRecordingSupported={isRecordingSupported}
              disabled={voiceButtonDisabled}
              disabledReason={voiceButtonDisabledReason}
              onClick={toggleVoice}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flexShrink: 0 }}
            />

            {/* Voice settings */}
            {isTTSSupported && !hideHeader && (
              <VoiceSettings
                ttsVoice={ttsVoice}
                onVoiceChange={setTtsVoice}
                voiceRate={voiceRate}
                onRateChange={setVoiceRate}
                disabled={isVoiceOn}
              />
            )}

            {isExecuting ? (
              <Button
                variant="outlined"
                color="error"
                endIcon={<StopIcon />}
                onClick={handleStopExecution}
                disabled={!currentExecutionId}
                sx={{ 
                  minWidth: hideHeader ? 44 : { xs: 'auto', md: 120 },
                  height: hideHeader ? 40 : { xs: 40, md: 56 },
                  borderRadius: hideHeader ? 1.5 : 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  fontWeight: 600,
                  borderWidth: 2,
                  px: { xs: 1.5, md: 2 },
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)',
                  },
                }}
              >
                {hideHeader ? null : 'Stop'}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSendMessage}
                disabled={(!inputMessage.trim() && pendingAttachments.length === 0) || !isConnected || isUploading || agentChatAccess.disabled}
                sx={{ 
                  minWidth: hideHeader ? 44 : { xs: 'auto', md: 120 },
                  height: hideHeader ? 40 : { xs: 40, md: 56 },
                  borderRadius: hideHeader ? 1.5 : 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  fontWeight: 600,
                  px: { xs: 1.5, md: 2 },
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
                title={agentChatAccess.disabled ? agentChatAccess.blockedMessage : undefined}
              >
                {hideHeader ? null : 'Send'}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentChat;
